// OverviewFeeds.tsx — the live data feeds half of the commit-overview pane
// (requirements 5 and 6).
//
// A feed is a run that streams per-step curves (today's `runs` +
// `run_points`). The pane draws small multiples: one chart per curve metric
// present across the selected commits' feeds, every chart drawing the same
// feeds in the same colors, so "the feed on commit B diverges at step 4k on
// val_loss but not train_loss" reads straight across. Each chart is followed
// by its summary table — tail mean and best value, each diffed against the
// first feed (the baseline).
//
// This is the rendering the deleted live-metrics page carried, moved next to
// the metrics table it should always have been read beside.
import { useMemo, useState } from 'react';
import type { CommitOverview } from '../api';
import { seriesColor, SERIES_SLOTS } from './colors';
import { feedLabel, flattenFeeds, MAX_FEEDS } from './feeds';
import { useThemeMode } from './theme';
import FeedChart from './FeedChart';

interface Props {
  owner: string;
  name: string;
  commits: CommitOverview[];
  /** Bumped by the pane's Live switch to refetch curves in place. */
  tick: number;
}

export default function OverviewFeeds({ owner, name, commits, tick }: Props) {
  const mode = useThemeMode();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [smooth, setSmooth] = useState(0);
  const [logY, setLogY] = useState(false);

  // Feeds keep the slot they were given by selection order, so adding a
  // commit never repaints the feeds already on screen.
  const feeds = flattenFeeds(commits);
  const totalFeeds = commits.reduce((n, c) => n + (c.feeds?.length ?? 0), 0);

  const metricNames = useMemo(() => {
    const names = new Set<string>();
    for (const f of feeds) for (const m of f.run.metrics ?? []) names.add(m.name);
    return Array.from(names).sort();
    // feeds is rebuilt every render; its identity is the commit list's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commits]);

  if (feeds.length === 0) {
    return (
      <div className="text-secondary">
        No feeds on the selected commit{commits.length === 1 ? '' : 's'}. A
        training loop posts them to <code>POST /runs</code> with the commit's
        sha; re-posting the same sha and run name appends steps.
      </div>
    );
  }

  const shown = metricNames.filter((n) => !hidden.has(n));
  const toggle = (n: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  return (
    <>
      <div className="feeds-controls">
        <span className="text-secondary">Feeds:</span>
        {feeds.map((f) => (
          <span key={f.run.id} className="feeds-chip">
            <span className="feeds-swatch" style={{ background: seriesColor(f.slot, mode) }} />
            {feedLabel(f)}
          </span>
        ))}
        {feeds.length > SERIES_SLOTS && (
          <span className="text-secondary">
            Colors repeat past {SERIES_SLOTS} feeds.
          </span>
        )}
        {totalFeeds > MAX_FEEDS && (
          <span className="text-secondary">
            Showing the first {MAX_FEEDS} of {totalFeeds} feeds — narrow the
            commit selection to see the rest.
          </span>
        )}
      </div>
      <div className="feeds-controls">
        <span className="text-secondary">Curves:</span>
        {metricNames.map((n) => (
          <label key={n} className="feeds-chip">
            <input type="checkbox" checked={!hidden.has(n)} onChange={() => toggle(n)} />
            <code>{n}</code>
          </label>
        ))}
        <label className="feeds-chip">
          <span className="text-secondary">Smoothing</span>
          <input
            type="range"
            min={0}
            max={0.99}
            step={0.01}
            value={smooth}
            onChange={(e) => setSmooth(Number(e.target.value))}
          />
          <code>{smooth.toFixed(2)}</code>
        </label>
        <label className="feeds-chip">
          <input type="checkbox" checked={logY} onChange={(e) => setLogY(e.target.checked)} />
          Log scale
        </label>
      </div>
      {shown.length === 0 ? (
        <div className="text-secondary">Every curve is hidden.</div>
      ) : (
        <div className="feeds-grid">
          {shown.map((m) => (
            <FeedChart
              key={m}
              owner={owner}
              name={name}
              metric={m}
              feeds={feeds}
              mode={mode}
              smooth={smooth}
              logY={logY}
              tick={tick}
            />
          ))}
        </div>
      )}
    </>
  );
}

